// ScreenerAnnContext.imp.js
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback
} from "react";
import { apiFetch } from "apiClient";
import { apiFetch as fetch } from "apiClient";
import WebSocketService from "../../../websockets/WebSocketService";
import { pfSymbolsNormalizedRef } from "@dashboard/shared/pfSymbolsRef";

const ScreenerAnnContext = createContext(null);
export const useScreenerAnnouncementFeed = () => useContext(ScreenerAnnContext);

const idOf = (a) => (a?.id != null ? String(a.id) : null);
const idStr = (v) => (v == null ? null : String(v));

// ---------- helpers ----------
const norm = (s) => (s ?? "").toString().toLowerCase();
const esc  = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// ANY substring (words)
const hasAny = (text, arr) => {
    if (!arr?.length) return true; // empty list => pass stage
    const t = norm(text);
    return arr.some(w => t.includes(w.toLowerCase()));
};

// ANY WHOLE word (words)
const hasWholeAny = (text, arr) => {
    if (!arr?.length) return true;
    const t = norm(text);
    return arr.some(w => new RegExp(`\\b${esc(w.toLowerCase())}\\b`, "i").test(t));
};

// ANY regex (phrases/regex)
const matchesRegexAny = (text, regexList) => {
    if (!regexList?.length) return true; // for Stage 1 includes: empty => pass
    return regexList.some((pattern) => {
        try {
            const re = new RegExp(pattern, "i"); // case-insensitive
            return re.test(text);
        } catch (e) {
            console.error("Invalid regex pattern:", pattern, e);
            return false;
        }
    });
};

// NEGATIONS for excludes
const notHasAny       = (text, arr) => !arr?.length || arr.every(w => !norm(text).includes(w.toLowerCase()));
const notHasWholeAny  = (text, arr) => !arr?.length || arr.every(w => !new RegExp(`\\b${esc(w.toLowerCase())}\\b`, "i").test(norm(text)));
const notMatchesRegexAny = (text, regexList) => !regexList?.length || regexList.every((pattern) => {
    try {
        const re = new RegExp(pattern, "i");
        return !re.test(text);
    } catch (e) {
        console.error("Invalid regex pattern:", pattern, e);
        return true;
    }
});

// 3-stage predicate per user spec: Include -> Exclude -> Force-rescue
// Final = IncludeMatch && (!ExcludeMatch || ForceMatch)
const makePredicateFromRules = (rules = {}) => {
    const {
        // Stage 1: MUST contain
        includeWords = [],
        includeWholeWords = [],
        includePhrases = [], // regex-capable

        // Stage 2: MUST NOT contain
        excludeWords = [],
        excludeWholeWords = [],
        excludePhrases = [], // regex-capable

        // Stage 3: FORCE include (rescue)
        forceIncludeWords = [],
        forceIncludeWholeWords = [],
        forceIncludePhrases = [], // regex-capable

        // Fields
        fields = ["subject", "summary"],
    } = rules;

    return (a) => {
        const subject = a?.announcementSubject ?? "";
        const summary = a?.aiAnnouncementSummary ?? "";
        const company = a?.screenerCompanyName ?? "";

        const parts = [];
        if (fields.includes("subject")) parts.push(subject);
        if (fields.includes("summary")) parts.push(summary);
        if (fields.includes("company")) parts.push(company);
        const text = parts.join(" ");

        const includeMatch =
            hasAny(text, includeWords) &&
            hasWholeAny(text, includeWholeWords) &&
            matchesRegexAny(text, includePhrases);

        if (!includeMatch) return false;

        const excludeMatch =
            !notHasAny(text, excludeWords) ||
            !notHasWholeAny(text, excludeWholeWords) ||
            !notMatchesRegexAny(text, excludePhrases);

        if (!excludeMatch) return true;

        const forceMatch =
            (forceIncludeWords.length && hasAny(text, forceIncludeWords)) ||
            (forceIncludeWholeWords.length && hasWholeAny(text, forceIncludeWholeWords)) ||
            (forceIncludePhrases.length && matchesRegexAny(text, forceIncludePhrases));

        return !!forceMatch;
    };
};

// Static tabs: PF, then Imp Concalls, then OTHER (first-match wins)
const staticTabDefs = [
    {
        key: "PF",
        name: "PF Announcements",
        type: "pf",
        test: (a) => a?.symbol && new Set(pfSymbolsNormalizedRef.current || []).has(a.symbol),
    },
    {
        key: "IMPCONCALLS",
        name: "Imp Concalls",
        type: "impconcalls",
        test: (a) => a?._kind === "impConcall", // only items we tag as impConcall
    },
    {
        key: "OTHER",
        name: "Other Announcements",
        type: "other",
        test: () => true,
    },
];

// NOTE: The order of tabRegistry affects filtering placement (first-match wins in rebuildBuckets).
// We persist user-chosen order in localStorage, but we ALWAYS keep "Other Announcements" last,
// otherwise it would swallow all announcements because its predicate is always true.
const TAB_ORDER_LS_KEY = "screener_annc_tab_order_v1";

export const ScreenerAnnouncementFeedProvider = ({ children, maxItems = 500 }) => {
    const [feed, setFeed] = useState([]);
    const [impConcalls, setImpConcalls] = useState([]);
    const [buckets, setBuckets] = useState({});
    const [hiddenIds, setHiddenIds] = useState(new Set());
    const [hasScreenerAnnouncementsLoaded, setHasScreenerAnnouncementsLoaded] = useState(false);
    const [dynamicDefs, setDynamicDefs] = useState([]);
    const [tabOrderLoaded, setTabOrderLoaded] = useState(false);

    // Persisted tab ordering (array of tab keys)
    const [tabOrder, setTabOrder] = useState([]);

    const seen = useRef(new Set());
    const makeKey = (a) => (a?.id != null ? `id:${a.id}` : null);

    const baseTabRegistry = useMemo(() => {
       // console.log("pfSymbolsNormalizedRef.current =", pfSymbolsNormalizedRef.current);

        const compiledUserDefs = dynamicDefs.map(d => ({
            key: `user_${d.id}`,
            name: d.name,
            type: "filter",
            test: makePredicateFromRules(safeParseJson(d.rules || "{}")),
            _id: d.id,
        }));
        return [
            staticTabDefs[0],
            staticTabDefs[1], // Imp Concalls
            ...compiledUserDefs,
            staticTabDefs[2], // Other (catch-all)
        ];
    }, [dynamicDefs]);

    const persistTabOrder = useCallback((order) => {
        if (!order || !order.length) return;
        const payload = order.map(String);

        // 1) localStorage – fast local reload
        try {
            localStorage.setItem(TAB_ORDER_LS_KEY, JSON.stringify(payload));
        } catch (e) {
            console.warn("Failed to persist tab order to localStorage", e);
        }

        // 2) backend – cross-device persistence
        (async () => {
            try {
                const res = await fetch("/api/screenerannouncements/tabOrder", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),   // ← FIXED
                });
                if (!res.ok) {
                    console.error("Backend did not accept tab order, status=", res.status);
                }
            } catch (e) {
                console.error("Failed to persist tab order to backend", e);
            }
        })();
    }, []);

    // Load tab order once (backend first, then localStorage fallback)
    useEffect(() => {
        (async () => {
            let stored = [];

            // 1) backend
            try {
                const res = await fetch("/api/screenerannouncements/tabOrder");
                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data)) {
                        stored = data.map(String);
                    } else if (data && Array.isArray(data.order)) {
                        stored = data.order.map(String);
                    }
                }
            } catch (e) {
                console.error("Failed to load tab order from backend", e);
            }

            // 2) fallback to localStorage
            if (!stored.length) {
                try {
                    const raw = localStorage.getItem(TAB_ORDER_LS_KEY);
                    const parsed = raw ? JSON.parse(raw) : null;
                    if (Array.isArray(parsed)) {
                        stored = parsed.map(String);
                    }
                } catch (e) {
                    console.warn("Failed to parse tab order from localStorage", e);
                }
            }

            if (stored.length) {
                const withoutOther = stored.filter(k => k !== "OTHER");
                const next = [...withoutOther, "OTHER"];
                setTabOrder(next);
            } else {
                // explicit empty, so reconcile later can build default
                setTabOrder([]);
            }

            // 🔴 IMPORTANT: mark that we’re done loading initial order
            setTabOrderLoaded(true);
        })();
    }, []);


    // Reconcile order whenever tabs list changes (new/deleted dynamic tabs)
    useEffect(() => {
        // 🚫 Don’t reconcile / persist until initial load is finished
        if (!tabOrderLoaded) return;

        const keys = baseTabRegistry.map(t => String(t.key));
        setTabOrder(prev => {
            // keep only existing keys
            let next = (prev || []).filter(k => keys.includes(String(k)));

            // append any new keys at end (preserve current order for existing ones)
            for (const k of keys) {
                if (!next.includes(String(k))) next.push(String(k));
            }

            // enforce "OTHER" last
            next = next.filter(k => k !== "OTHER").concat("OTHER");

            // now it's safe to persist
            persistTabOrder(next);
            return next;
        });
    }, [baseTabRegistry, persistTabOrder, tabOrderLoaded]);

    // Apply ordering to registry (keeps "OTHER" last no matter what)
    const tabRegistry = useMemo(() => {
        const byKey = new Map(baseTabRegistry.map(t => [String(t.key), t]));
        const ordered = [];

        // Use tabOrder first
        for (const k of (tabOrder || [])) {
            const def = byKey.get(String(k));
            if (def) ordered.push(def);
        }

        // Add anything missing (should be rare due to reconcile, but safe)
        for (const def of baseTabRegistry) {
            if (!ordered.some(x => x.key === def.key)) ordered.push(def);
        }

        // Enforce OTHER last
        const withoutOther = ordered.filter(t => t.key !== "OTHER");
        const other = ordered.find(t => t.key === "OTHER") || baseTabRegistry.find(t => t.key === "OTHER");
        return other ? [...withoutOther, other] : withoutOther;
    }, [baseTabRegistry, tabOrder]);

    const visibleFeed = useMemo(() => {
        return feed.filter(a => {
            const id = idOf(a);
            return !id || !hiddenIds.has(id);
        });
    }, [feed, hiddenIds]);

    const visibleImp = useMemo(() => {
        return impConcalls.filter(a => {
            const id = idOf(a);
            return !id || !hiddenIds.has(id);
        });
    }, [impConcalls, hiddenIds]);

    const rebuildBuckets = (items, impItems) => {
        const result = {};
        for (const def of tabRegistry) result[def.name] = [];

        // place normal announcements via registry (first-match wins)
        for (const a of items) {
            let placed = false;
            for (const def of tabRegistry) {
                try {
                    if (def.type === "impconcalls") continue; // handled separately
                    if (def.test(a)) {
                        result[def.name].push(a);
                        placed = true;
                        break;
                    }
                } catch (e) {
                    console.error("Tab predicate error:", def, e);
                }
            }
            if (!placed) {
                (result["Other Announcements"] ||= []).push(a);
            }
        }

        // direct drop of imp concalls into its tab (order doesn't change this)
        result["Imp Concalls"] = [...impItems];
        setBuckets(result);
    };


    useEffect(() => {
        rebuildBuckets(visibleFeed, visibleImp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabRegistry, visibleFeed, visibleImp]);

    // ---- Tab reorder API (used by drag/drop UI) ----
    const moveTab = (dragKey, hoverKey) => {
        if (!dragKey || !hoverKey) return;
        const dk = String(dragKey);
        const hk = String(hoverKey);
        if (dk === hk) return;
        // don't allow moving OTHER away from last (we enforce it anyway)
        if (dk === "OTHER") return;

        setTabOrder(prev => {
            const cur = (prev && prev.length) ? [...prev] : tabRegistry.map(t => String(t.key));
            const withoutOther = cur.filter(k => k !== "OTHER");

            const from = withoutOther.indexOf(dk);
            const to = withoutOther.indexOf(hk);
            if (from < 0 || to < 0) return prev;

            withoutOther.splice(to, 0, withoutOther.splice(from, 1)[0]);
            const next = [...withoutOther, "OTHER"];

            // persist via helper
            persistTabOrder(next);
            return next;
        });
    };

    const resetTabOrder = () => {
        const keys = baseTabRegistry.map(t => String(t.key));
        const next = keys.filter(k => k !== "OTHER").concat("OTHER");
        setTabOrder(next);
        // persist via helper
        persistTabOrder(next);
    };

    // ---- Load dynamic tabs on mount ----
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch("/api/screenerannouncements/tabs");
                const data = await res.json();
                setDynamicDefs(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Failed to load dynamic tabs", e);
                setDynamicDefs([]);
            }
        })();
    }, []);

    // ---- Initial feed load (screener announcements) ----
// ---- Initial feed load (screener announcements) ----
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const res = await fetch("/api/screenerannouncements/allUnRead");
                const json = await res.json();
                if (cancelled) return;

                const items = Array.isArray(json) ? json : [];
                console.log("screener allUnRead count=", items.length, "sample=", items[0]);

                // 1) render immediately (no holders)
                seen.current.clear();
                const next = [];
                for (const a of items) {
                    const k = makeKey(a);
                    if (!k || seen.current.has(k)) continue;
                    seen.current.add(k);

                    next.push({
                        ...a,
                        superstarHolders: [], // <-- important: do NOT block UI on holders
                    });
                }

                setFeed(next.slice(0, maxItems));
                console.log("screener feed loaded (without holders) =", next.length);

                // 2) fetch holders in background and patch-in
                const symbols = Array.from(new Set(items.map(a => a.symbol).filter(Boolean)));
                if (!symbols.length) return;

                try {
                    const resH = await fetch(`/api/screener-results/holders`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ symbols }),
                    });

                    if (!resH.ok) {
                        console.error("holders API non-OK status=", resH.status);
                        return;
                    }

                    const holdersMap = (await resH.json()) || {};
                    if (cancelled) return;

                    setFeed(prev =>
                        (prev || []).map(a => ({
                            ...a,
                            superstarHolders: holdersMap[a.symbol] || a.superstarHolders || [],
                        }))
                    );

                    console.log("screener holders patched");
                } catch (e) {
                    console.error("holders fetch/parse failed (screener)", e);
                }
            } catch (e) {
                console.error("Failed to load screener announcements", e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [maxItems]);


    // ---- Initial load (Imp Concalls) ----
    // ---- Initial load (Imp Concalls) ----
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const res = await fetch("/api/impconcalls/allUnRead");
                const json = await res.json();
                if (cancelled) return;

                const items = Array.isArray(json) ? json : [];

                // 1) render immediately (no holders)
                const list = items.map(a => ({
                    ...a,
                    _kind: "impConcall",
                    superstarHolders: [], // <-- do NOT block UI on holders
                }));

                setImpConcalls(list);
                console.log("imp concalls loaded (without holders) =", list.length);

                // 2) fetch holders in background and patch-in
                const symbols = Array.from(new Set(items.map(a => a.symbol).filter(Boolean)));
                if (!symbols.length) return;

                try {
                    const resH = await fetch(`/api/screener-results/holders`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ symbols }),
                    });

                    if (!resH.ok) {
                        console.error("holders API non-OK status=", resH.status);
                        return;
                    }

                    const holdersMap = (await resH.json()) || {};
                    if (cancelled) return;

                    setImpConcalls(prev =>
                        (prev || []).map(a => ({
                            ...a,
                            superstarHolders: holdersMap[a.symbol] || a.superstarHolders || [],
                        }))
                    );

                    console.log("imp concalls holders patched");
                } catch (e) {
                    console.error("holders fetch/parse failed (imp concalls)", e);
                }
            } catch (e) {
                console.error("Failed to load imp concalls", e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

// ---- Live updates via WS ----
    useEffect(() => {
        const onScreener = (msg) => {
            const items =
                msg?.data?.announcements ||
                msg?.data?.items ||
                msg?.items ||
                [];

            if (!items.length) {
                // TEMP debug (remove later)
                console.log("WS screenerAnnouncements msg but no items found:", msg);
                return;
            }

            setFeed((prev) => {
                const next = [...prev];
                for (const a of items) {
                    const k = makeKey(a);
                    if (k && seen.current.has(k)) continue;
                    if (k) seen.current.add(k);
                    next.unshift(a);
                }
                if (next.length > maxItems) next.length = maxItems;
                return next;
            });
        };

        const onImp = (msg) => {
            const items =
                msg?.data?.alerts ||
                msg?.data?.items ||
                msg?.items ||
                [];

            if (!items.length) {
                console.log("WS impConcalls msg but no items found:", msg);
                return;
            }

            setImpConcalls((prev) => {
                const next = [...prev];
                for (const a of items) {
                    const id = idOf(a);
                    if (id && prev.some((x) => idOf(x) === id)) continue;
                    next.unshift({ ...a, _kind: "impConcall" });
                }
                return next;
            });
        };

        WebSocketService.subscribe("screenerAnnouncements", null, onScreener);
        WebSocketService.subscribe("impConcalls", null, onImp);

        setHasScreenerAnnouncementsLoaded(true);

        return () => {
            WebSocketService.unsubscribe?.("screenerAnnouncements", null, onScreener);
            WebSocketService.unsubscribe?.("impConcalls", null, onImp);
        };
    }, [maxItems]);


    // ---- Mark read ----
    const markOld = (ids) => {
        if (!ids?.length) return;

        // normalize incoming list
        const normIds = ids.map(idStr).filter(Boolean);

        // SAFETY: if something tries to hide *everything*, don't.
        // (This is the typical reason "it worked yesterday, broke today".)
        if (normIds.length > 1000) {
            console.warn("markOld called with too many ids, ignoring:", normIds.length);
            return;
        }

        setHiddenIds(prev => {
            const n = new Set(prev);
            normIds.forEach(i => n.add(i));
            return n;
        });
    };

    const markImpConcallsRead = async (ids) => {
        if (!ids?.length) return;

        const normIds = ids.map(idStr).filter(Boolean);

        // Hide in UI immediately (consistent with visibleImp filter)
        setHiddenIds(prev => {
            const n = new Set(prev);
            normIds.forEach(i => n.add(i));
            return n;
        });

        // Also remove from impConcalls state for good measure
        setImpConcalls(prev =>
            prev.filter(x => !normIds.includes(idOf(x)))
        );

        try {
            await fetch("/api/impconcalls/markRead", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(normIds),
            });
        } catch (e) {
            console.error(e);
        }
    };


    const clear = () => {
        seen.current.clear();
        setFeed([]);
        setImpConcalls([]);
        setHiddenIds(new Set());
        setBuckets({});
    };

    // ---- Create / update / delete dynamic (user-defined) tabs ----
    const addDynamicTab = async ({ name, rules }) => {
        if (!name) throw new Error("Tab name is required");

        const body = {
            name,
            rules: JSON.stringify(rules ?? {}),
        };

        try {
            const res = await fetch("/api/screenerannouncements/tabs", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            const created = await res.json();

            setDynamicDefs(prev => [
                ...prev,
                { id: created.id, name: created.name, rules: created.rules }
            ]);

        } catch (err) {
            console.error("Failed to create dynamic tab:", err);
        }
    };

    const updateDynamicTab = async (key, { name, rules }) => {
        const def = dynamicDefs.find(d => `user_${d.id}` === key);
        if (!def) return;

        const body = {
            name,
            rules: JSON.stringify(rules ?? {}),
        };

        try {
            const res = await fetch(`/api/screenerannouncements/tabs/${def.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            const updated = await res.json();

            setDynamicDefs(prev =>
                prev.map(d =>
                    d.id === def.id ? { id: updated.id, name: updated.name, rules: updated.rules } : d
                )
            );

        } catch (err) {
            console.error("Failed to update dynamic tab:", err);
        }
    };

    const removeDynamicTab = async (key) => {
        const def = dynamicDefs.find(d => `user_${d.id}` === key);
        if (!def) return;

        try {
            await fetch(`/api/screenerannouncements/tabs/${def.id}`, {
                method: "DELETE",
            });

            setDynamicDefs(prev => prev.filter(d => d.id !== def.id));

        } catch (err) {
            console.error("Failed to delete dynamic tab:", err);
        }
    };

    const api = useMemo(() => ({
        feed,
        buckets,
        markOld,
        markImpConcallsRead,

        // ordered registry (drag/drop affects this)
        tabRegistry,

        // order controls
        moveTab,
        resetTabOrder,

        dynamicDefs,
        clear,
        hasScreenerAnnouncementsLoaded,
        addDynamicTab,
        updateDynamicTab,
        removeDynamicTab
    }), [feed, buckets, tabRegistry, dynamicDefs, hasScreenerAnnouncementsLoaded]);

    return (
        <ScreenerAnnContext.Provider value={api}>
            {children}
        </ScreenerAnnContext.Provider>
    );
};

function safeParseJson(text) {
    try { return JSON.parse(text); } catch { return {}; }
}

export default ScreenerAnnContext;
